import { computeTokenAmountInUsd } from '@repo/web3/utils'

import { isVaultV2DustAllocation } from '@/helpers/vaultV2DustMarket'

import type { AllocationAsset, AllocationSeries } from './allocationHistory.contract'

/**
 * The series the allocation history chart draws. A market drops out only when it was dust at
 * every point, its live allocation is dust too, and its cap stack is not fully open, so an exited
 * market's past exposure stays on the chart. Unknown amounts, prices or cap rows are never dust.
 */
export function getChartedCapItems({
  series,
  asset
}: {
  series: readonly AllocationSeries[]
  asset: Pick<AllocationAsset, 'decimals' | 'priceUsd'>
}): AllocationSeries[] {
  const toUsd = (amount: bigint) =>
    asset.decimals === null
      ? null
      : (computeTokenAmountInUsd(amount, asset.decimals, asset.priceUsd ?? undefined) ?? null)
  const isDust = (allocation: bigint) =>
    isVaultV2DustAllocation({ allocation, allocationUsd: toUsd(allocation) })

  // Summed per market and timestamp across adapters, like the other surfaces.
  const totalsByMarket = new Map<string, Map<number, bigint>>()
  for (const item of series) {
    if (item.target.kind !== 'market') continue
    const totals = totalsByMarket.get(item.target.marketId) ?? new Map<number, bigint>()
    for (const point of item.points) {
      totals.set(point.timestamp, (totals.get(point.timestamp) ?? 0n) + BigInt(point.allocation))
    }
    totalsByMarket.set(item.target.marketId, totals)
  }

  return series.filter(item => {
    if (item.target.kind !== 'market') return true
    const { marketId, current } = item.target
    // An empty history is unknown, not dust: the pipeline may not have emitted a point yet.
    const totals = [...(totalsByMarket.get(marketId)?.values() ?? [])]
    const alwaysDust = totals.length > 0 && totals.every(isDust)
    // History can lag a fresh allocation, so the live allocation must be dust too.
    const currentDust = current.complete && isDust(BigInt(current.allocation))
    return !alwaysDust || !currentDust || current.fullyOpen
  })
}
